import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Send, MessageSquare, Mail, Phone, Clock, CheckCircle2, XCircle, Loader2, Paperclip, X, Star } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';

const ClientCommunicationTab = ({ client, clientId }) => {
  const [messages, setMessages] = useState([]);
  const [channel, setChannel] = useState('whatsapp');
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [sendingSurvey, setSendingSurvey] = useState(false);
  const fileRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/messages`);
      setMessages(res.data);
    } catch { /* ignore */ }
  }, [clientId]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  const handleSend = async () => {
    if (!message.trim() && !attachment) { toast.error('Ecrivez un message ou joignez un fichier'); return; }
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('channel', channel);
      formData.append('message', message.trim());
      if (channel === 'email') formData.append('subject', subject || 'Message de votre garage');
      if (attachment) formData.append('file', attachment);

      const res = await axios.post(`${API}/clients/${clientId}/send-message`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        // Only open wa.me when the Cloud API couldn't deliver (fallback)
        // If method === 'api', the message is already sent — DO NOT open wa.me (double send).
        if (channel === 'whatsapp' && res.data.method !== 'api' && res.data.whatsapp_url) {
          window.open(res.data.whatsapp_url, '_blank');
          toast.success('WhatsApp Web ouvert — envoyez le message depuis votre navigateur');
        } else {
          toast.success(`Message envoye par ${channel === 'whatsapp' ? 'WhatsApp' : 'Email'}`);
        }
        setMessage('');
        setSubject('');
        setAttachment(null);
        if (fileRef.current) fileRef.current.value = '';
        loadMessages();
      } else {
        toast.error(res.data.error || 'Echec envoi');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi');
    } finally { setSending(false); }
  };

  const hasPhone = client?.phone;
  const hasEmail = client?.email;

  const handleSendSurvey = async () => {
    setSendingSurvey(true);
    try {
      const res = await axios.post(`${API}/satisfaction/send-to-client/${clientId}`, {});
      if (res.data.success) {
        const ch = res.data.channel === 'whatsapp' ? 'WhatsApp' : 'Email';
        toast.success(`Enquete de satisfaction envoyee par ${ch}`);
      } else {
        toast.error('Echec envoi enquete');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi enquete');
    } finally { setSendingSurvey(false); }
  };

  return (
    <div className="space-y-4" data-testid="communication-tab">
      {/* Quick actions */}
      <Card className="p-3 rounded-xl flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold text-slate-500 mr-2">Actions rapides :</span>
        <Button size="sm" variant="outline" onClick={handleSendSurvey} disabled={sendingSurvey || (!hasPhone && !hasEmail)} className="text-xs h-7" data-testid="send-survey-btn">
          {sendingSurvey ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Star className="w-3 h-3 mr-1 text-amber-500" />}
          Enquete de satisfaction
        </Button>
      </Card>

      <Card className="p-4 rounded-xl space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-sm font-bold text-slate-900">Envoyer un message</h3>
          <div className="flex gap-1 ml-auto">
            <Button size="sm" variant={channel === 'whatsapp' ? 'default' : 'outline'}
              onClick={() => setChannel('whatsapp')} disabled={!hasPhone}
              className={`text-xs h-7 ${channel === 'whatsapp' ? 'bg-green-600 hover:bg-green-700' : ''}`}
              data-testid="channel-whatsapp">
              <MessageSquare className="w-3 h-3 mr-1" /> WhatsApp
            </Button>
            <Button size="sm" variant={channel === 'email' ? 'default' : 'outline'}
              onClick={() => setChannel('email')} disabled={!hasEmail}
              className={`text-xs h-7 ${channel === 'email' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
              data-testid="channel-email">
              <Mail className="w-3 h-3 mr-1" /> Email
            </Button>
          </div>
        </div>

        {!hasPhone && !hasEmail && (
          <p className="text-sm text-red-500 bg-red-50 p-2 rounded">Ce client n'a ni telephone ni email.</p>
        )}

        <div className="flex items-center gap-2 text-xs text-slate-500">
          {hasPhone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{client.phone}</span>}
          {hasEmail && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{client.email}</span>}
        </div>

        {channel === 'email' && (
          <Input value={subject} onChange={e => setSubject(e.target.value)}
            placeholder="Objet du mail" className="h-8 text-sm" data-testid="email-subject" />
        )}

        <Textarea value={message} onChange={e => setMessage(e.target.value)}
          placeholder={channel === 'whatsapp' ? 'Votre message WhatsApp...' : 'Votre message email...'}
          rows={4} data-testid="message-input" />

        {/* Attachment */}
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" className="hidden"
            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
            onChange={e => setAttachment(e.target.files?.[0] || null)} data-testid="attach-file-input" />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="text-xs h-7" data-testid="attach-file-btn">
            <Paperclip className="w-3 h-3 mr-1" /> Piece jointe
          </Button>
          {attachment && (
            <div className="flex items-center gap-1.5 bg-blue-50 px-2 py-1 rounded text-xs text-blue-700">
              <Paperclip className="w-3 h-3" />
              <span className="truncate max-w-[200px]">{attachment.name}</span>
              <button onClick={() => { setAttachment(null); if (fileRef.current) fileRef.current.value = ''; }}
                className="text-blue-400 hover:text-red-500" data-testid="remove-attachment">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          <div className="flex-1" />
          <Button onClick={handleSend} disabled={sending || (!message.trim() && !attachment)} className="bg-slate-800" data-testid="send-message-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Envoyer
          </Button>
        </div>
      </Card>

      {/* Message history */}
      <div>
        <h3 className="text-sm font-bold text-slate-900 mb-2">Historique ({messages.length})</h3>
        {messages.length === 0 ? (
          <Card className="p-6 text-center rounded-xl">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-400">Aucun message envoye</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {messages.map(m => (
              <Card key={m.id} className="p-3 rounded-lg" data-testid={`msg-${m.id}`}>
                <div className="flex items-start gap-2">
                  {m.channel === 'whatsapp' ? (
                    <MessageSquare className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                  ) : (
                    <Mail className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-[10px] ${m.channel === 'whatsapp' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {m.channel === 'whatsapp' ? 'WhatsApp' : 'Email'}
                      </Badge>
                      {m.status === 'sent' ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-red-500" />}
                      {m.attachment_name && (
                        <Badge className="text-[10px] bg-slate-100 text-slate-600"><Paperclip className="w-2.5 h-2.5 mr-0.5" />{m.attachment_name}</Badge>
                      )}
                      <span className="text-[10px] text-slate-400 ml-auto">
                        <Clock className="w-3 h-3 inline mr-0.5" />{m.sent_at?.slice(0, 16).replace('T', ' ')}
                      </span>
                    </div>
                    {m.subject && <p className="text-xs font-medium text-slate-700 mb-0.5">Objet: {m.subject}</p>}
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{m.message}</p>
                    {m.error && <p className="text-xs text-red-500 mt-1">Erreur: {m.error}</p>}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientCommunicationTab;
